import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { GpsService, GpsResult } from './gps.service';
import { ConnectivityService } from './connectivity.service';
import { OfflineQueueService, QueuedAction } from './offline-queue.service';

export interface ClockPayload {
  type: 'CLOCK_IN' | 'CLOCK_OUT';
  timestamp: string;
  lat: number | null;
  lng: number | null;
  location_unavailable: boolean;
  gps_denied: boolean;
}

export interface ClockResponse {
  ok: boolean;
  event_id?: string;
  error?: string;
  queued?: boolean;
}

/**
 * ClockService manages clock in/out actions with GPS capture.
 *
 * Responsibilities:
 * - Track current clock state (clocked in/out) via signal
 * - Persist clock state to localStorage so it survives page refreshes
 * - Capture GPS position before each clock action
 * - POST clock events to the backend API when online
 * - Queue clock events in IndexedDB when offline
 *
 * Validates: Requirements 4.1, 4.2, 4.3, 9.3
 */
@Injectable({ providedIn: 'root' })
export class ClockService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly gps = inject(GpsService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly offlineQueue = inject(OfflineQueueService);

  private static readonly CLOCK_STATE_KEY = 'pwa-clock-state';

  /** Whether the resource is currently clocked in. */
  readonly clockedIn = signal(this.loadPersistedState());

  /** Whether a clock action is currently in progress. */
  readonly isLoading = signal(false);

  /** Whether there are pending (unsynced) clock actions. */
  readonly hasPendingClockActions = signal(false);

  /**
   * Perform a clock-in action.
   * Captures GPS position, then either posts to the backend or queues offline.
   * Starts continuous location reporting on success.
   */
  async clockIn(): Promise<ClockResponse> {
    if (this.clockedIn()) {
      return { ok: false, error: 'Already clocked in' };
    }
    this.isLoading.set(true);
    try {
      const gpsResult = await this.gps.getCurrentPosition();
      const payload = this.buildPayload('CLOCK_IN', gpsResult);

      if (!this.connectivity.isOnline()) {
        const queued = await this.queueClockEvent(payload);
        if (queued) {
          this.setClockedIn(true);
          return { ok: true, queued: true };
        }
        return { ok: false, error: 'Failed to queue offline action' };
      }

      const response = await this.postClockEvent(payload);

      if (response.ok) {
        this.setClockedIn(true);
      }

      return response;
    } catch (error: any) {
      return { ok: false, error: error?.message || 'Clock in failed' };
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Perform a clock-out action.
   * Captures GPS position, then either posts to the backend or queues offline.
   * Stops continuous location reporting on success.
   */
  async clockOut(): Promise<ClockResponse> {
    if (!this.clockedIn()) {
      return { ok: false, error: 'Not clocked in' };
    }
    this.isLoading.set(true);
    try {
      const gpsResult = await this.gps.getCurrentPosition();
      const payload = this.buildPayload('CLOCK_OUT', gpsResult);

      if (!this.connectivity.isOnline()) {
        const queued = await this.queueClockEvent(payload);
        if (queued) {
          this.setClockedIn(false);
          return { ok: true, queued: true };
        }
        return { ok: false, error: 'Failed to queue offline action' };
      }

      const response = await this.postClockEvent(payload);

      if (response.ok) {
        this.setClockedIn(false);
      }

      return response;
    } catch (error: any) {
      return { ok: false, error: error?.message || 'Clock out failed' };
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Set the clock state directly (e.g., when restoring state from backend on login).
   * Persists to localStorage and manages continuous location reporting.
   */
  setClockedIn(value: boolean): void {
    this.clockedIn.set(value);
    this.persistState(value);
    if (value) {
      this.startLocationReporting();
    } else {
      this.gps.stopContinuousReporting();
    }
  }

  /**
   * Check if there are pending clock actions in the offline queue.
   * Used to prevent server state from overwriting optimistic local state.
   */
  async checkPendingClockActions(): Promise<boolean> {
    try {
      const clockInActions = await this.offlineQueue.getByType('CLOCK_IN');
      const clockOutActions = await this.offlineQueue.getByType('CLOCK_OUT');
      const hasPending = clockInActions.length > 0 || clockOutActions.length > 0;
      this.hasPendingClockActions.set(hasPending);
      return hasPending;
    } catch {
      return false;
    }
  }

  /**
   * Start continuous GPS location reporting using the current auth context.
   */
  private startLocationReporting(): void {
    const tenantId = this.auth.tenantId();
    const resourceId = this.auth.resourceId();
    if (tenantId && resourceId) {
      this.gps.startContinuousReporting(resourceId, tenantId);
    }
  }

  /** Build the clock event payload from GPS result. */
  private buildPayload(type: 'CLOCK_IN' | 'CLOCK_OUT', gpsResult: GpsResult): ClockPayload {
    return {
      type,
      timestamp: new Date().toISOString(),
      lat: gpsResult.lat,
      lng: gpsResult.lng,
      location_unavailable: gpsResult.location_unavailable,
      gps_denied: gpsResult.gps_denied,
    };
  }

  /** POST the clock event to the backend API. */
  private async postClockEvent(payload: ClockPayload): Promise<ClockResponse> {
    const tenantId = this.auth.tenantId();
    const resourceId = this.auth.resourceId();

    if (!tenantId || !resourceId) {
      return { ok: false, error: 'Not authenticated' };
    }

    const path = `/tenants/${tenantId}/resources/${resourceId}/clock`;
    return firstValueFrom(this.api.post<ClockResponse>(path, payload));
  }

  /**
   * Queue a clock event in IndexedDB for later sync.
   * Returns true if queued successfully.
   */
  private async queueClockEvent(payload: ClockPayload): Promise<boolean> {
    const tenantId = this.auth.tenantId();
    const resourceId = this.auth.resourceId();

    if (!tenantId || !resourceId) {
      return false;
    }

    const action: QueuedAction = {
      id: this.offlineQueue.generateId(),
      type: payload.type,
      tenant_id: tenantId,
      resource_id: resourceId,
      timestamp: payload.timestamp,
      lat: payload.lat,
      lng: payload.lng,
      location_unavailable: payload.location_unavailable,
      gps_denied: payload.gps_denied,
      created_at: new Date().toISOString(),
      retry_count: 0,
    };

    try {
      await this.offlineQueue.addAction(action);
      console.log(`[ClockService] Queued ${payload.type} action for offline sync (id: ${action.id})`);
      return true;
    } catch {
      return false;
    }
  }

  /** Load persisted clock state from localStorage. */
  private loadPersistedState(): boolean {
    try {
      return localStorage.getItem(ClockService.CLOCK_STATE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  /** Persist clock state to localStorage. */
  private persistState(value: boolean): void {
    try {
      localStorage.setItem(ClockService.CLOCK_STATE_KEY, value ? 'true' : 'false');
    } catch {
      // localStorage may not be available (private browsing)
    }
  }
}
